import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { IconCheck, IconDeviceFloppy, IconPlus, IconTrash } from '@tabler/icons-react';
import { useSnackbar } from 'notistack';

import { DesktopLayout } from 'src/layout';
import { ROUTES_URL } from 'src/constants/routes';
import AIManagersService, {
    AIManagerDisplayKnowledgeItem,
    AIManagerDisplayKnowledgeProfile,
    AIManagerDisplayKnowledgeSection,
    AIManagerKnowledgeDocument,
    AIManagerKnowledgeSectionKey,
    normalizeKnowledgeDisplayProfile,
} from 'src/services/AIManagersService';
import { history } from 'src/utils/history';

const BORDER = 'var(--lm-border)';
const CARD_RADIUS = '8px';
const formatFacts = (n: number) =>
    `${n} ${
        n % 100 >= 11 && n % 100 <= 14
            ? 'фактов'
            : n % 10 === 1
            ? 'факт'
            : n % 10 >= 2 && n % 10 <= 4
            ? 'факта'
            : 'фактов'
    }`;

const KNOWLEDGE_SECTIONS: Array<{ key: AIManagerKnowledgeSectionKey; title: string }> = [
    { key: 'offer', title: 'Что вы предлагаете' },
    { key: 'audience', title: 'Кому подходит' },
    { key: 'value', title: 'Почему выбирают' },
    { key: 'process', title: 'Как работает' },
    { key: 'prices', title: 'Цены и условия оплаты' },
    { key: 'constraints', title: 'Ограничения' },
    { key: 'contacts', title: 'Контакты' },
];
const KNOWLEDGE_PREVIEW_HEADINGS = new Set([
    'База знаний',
    'Профиль компании',
    ...KNOWLEDGE_SECTIONS.map((section) => section.title),
]);

const cardSx = {
    bgcolor: 'var(--lm-surface)',
    border: `1px solid ${BORDER}`,
    borderRadius: CARD_RADIUS,
    boxShadow: 'none',
} as const;

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: CARD_RADIUS,
        bgcolor: 'var(--lm-surface)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: BORDER,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--lm-text)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--lm-text)',
    },
} as const;

function createEmptyProfile(): AIManagerDisplayKnowledgeProfile {
    return {
        summary: '',
        sections: KNOWLEDGE_SECTIONS.map((section) => ({
            ...section,
            items: [],
        })),
        missingSections: KNOWLEDGE_SECTIONS.map((section) => section.key),
        warnings: [],
    };
}

function withCompleteSections(profile: AIManagerDisplayKnowledgeProfile | null): AIManagerDisplayKnowledgeProfile {
    const source = profile || createEmptyProfile();
    return {
        ...source,
        sections: KNOWLEDGE_SECTIONS.map((section) => {
            const current = source.sections.find((item) => item.key === section.key);
            return current || { ...section, items: [] };
        }),
    };
}

function sanitizeProfile(profile: AIManagerDisplayKnowledgeProfile) {
    return {
        ...profile,
        sections: profile.sections.map((section) => ({
            ...section,
            items: section.items
                .map((item) => ({
                    ...item,
                    text: cleanPreviewText(item.text),
                }))
                .filter((item) => item.text.trim()),
        })),
    };
}

function getProfileItemCount(profile: AIManagerDisplayKnowledgeProfile | null) {
    return withCompleteSections(profile).sections.reduce(
        (sum, section) => sum + section.items.filter((item) => item.text.trim()).length,
        0
    );
}

function cleanPreviewText(value?: string | null) {
    const text = String(value || '')
        .split(/\n+/)
        .map((line) =>
            line
                .replace(/#{1,6}\s*/g, '')
                .replace(/(^|\s)[-*]\s*/g, '$1')
                .replace(/\*\*/g, '')
                .trim()
        )
        .filter((line) => line && line !== '-')
        .join(' · ');
    let cleanedText = text;
    KNOWLEDGE_PREVIEW_HEADINGS.forEach((heading) => {
        const escapedHeading = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        cleanedText = cleanedText.replace(
            new RegExp(`(^|\\s*·\\s*|\\s+)${escapedHeading}(?=\\s|\\s*·\\s*|$)`, 'gi'),
            ' · '
        );
    });
    return cleanedText
        .split(/\s*·\s*/)
        .map((part) => part.trim())
        .filter((part) => part && !KNOWLEDGE_PREVIEW_HEADINGS.has(part))
        .join(' · ');
}

function getProfilePreview(profile: AIManagerDisplayKnowledgeProfile, fallback?: string | null) {
    const facts = withCompleteSections(profile)
        .sections.flatMap((section) => section.items.map((item) => cleanPreviewText(item.text)))
        .filter(Boolean)
        .slice(0, 3);
    return facts.length ? facts.join(' · ') : cleanPreviewText(fallback).slice(0, 240);
}

function getSourceLabel(value?: string | null) {
    return (
        String(value || '')
            .replace(/^https?:\/\//, '')
            .replace(/\/$/, '') || 'Без источника'
    );
}

function formatDate(value?: string | null) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function getProfileFromDocument(document: AIManagerKnowledgeDocument): AIManagerDisplayKnowledgeProfile {
    const metadata = document.metadata || {};
    const rawProfile =
        metadata.approved_profile ||
        metadata.approvedProfile ||
        metadata.knowledge_display_profile ||
        metadata.knowledgeDisplayProfile ||
        metadata.display_profile ||
        metadata.displayProfile;
    const normalized = normalizeKnowledgeDisplayProfile(rawProfile);
    if (getProfileItemCount(normalized) > 0) {
        return withCompleteSections(sanitizeProfile(normalized));
    }

    return profileFromMarkdown(document);
}

function profileFromMarkdown(document: AIManagerKnowledgeDocument): AIManagerDisplayKnowledgeProfile {
    const profile = createEmptyProfile();
    const titleToKey = new Map(KNOWLEDGE_SECTIONS.map((section) => [section.title.toLowerCase(), section.key]));
    let currentKey: AIManagerKnowledgeSectionKey | null = null;

    String(document.body || document.summary || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .forEach((line, index) => {
            const heading = line
                .replace(/^#+\s*/, '')
                .trim()
                .toLowerCase();
            if (titleToKey.has(heading)) {
                currentKey = titleToKey.get(heading) || null;
                return;
            }
            if (!currentKey || !line.startsWith('-')) {
                return;
            }
            const text = line.replace(/^-\s*/, '').trim();
            if (!text) {
                return;
            }
            profile.sections
                .find((section) => section.key === currentKey)
                ?.items.push({
                    id: `markdown-${currentKey}-${index}`,
                    text,
                    origin: 'website',
                    sourceUrl: document.sourceUrl,
                });
        });

    if (getProfileItemCount(profile) === 0 && (document.summary || document.body)) {
        profile.sections[0].items.push({
            id: `summary-${document.id}`,
            text: cleanPreviewText(document.summary || document.body).slice(0, 700),
            origin: 'manual',
            sourceUrl: document.sourceUrl,
            type: 'source_note',
        });
    }

    return profile;
}

function toBackendProfile(profile: AIManagerDisplayKnowledgeProfile) {
    const completed = withCompleteSections(profile);
    return {
        summary: buildProfileSummary(completed),
        sections: completed.sections.map((section) => ({
            key: section.key,
            title: section.title,
            items: section.items
                .filter((item) => item.text.trim())
                .map((item) => ({
                    id: item.id,
                    text: item.text.trim(),
                    origin: item.origin,
                    source_url: item.sourceUrl,
                    source_snippet: item.sourceSnippet,
                    confidence: item.confidence,
                    type: item.type,
                })),
        })),
        missing_sections: completed.sections
            .filter((section) => section.items.length === 0)
            .map((section) => section.key),
        warnings: completed.warnings,
    };
}

function buildProfileBody(profile: AIManagerDisplayKnowledgeProfile) {
    const lines = ['# База знаний', ''];
    withCompleteSections(profile).sections.forEach((section) => {
        lines.push(`## ${section.title}`);
        const items = section.items.filter((item) => item.text.trim());
        if (items.length) {
            items.forEach((item) => lines.push(`- ${item.text.trim()}`));
        } else {
            lines.push('-');
        }
        lines.push('');
    });
    return lines.join('\n').trim();
}

function buildProfileSummary(profile: AIManagerDisplayKnowledgeProfile) {
    return withCompleteSections(profile)
        .sections.filter((section) => section.items.some((item) => item.text.trim()))
        .map((section) => {
            const items = section.items
                .filter((item) => item.text.trim())
                .slice(0, 4)
                .map((item) => `- ${item.text.trim()}`)
                .join('\n');
            return `${section.title}:\n${items}`;
        })
        .join('\n\n')
        .trim();
}

function buildDisplayItem(sectionKey: AIManagerKnowledgeSectionKey, text: string): AIManagerDisplayKnowledgeItem {
    return {
        id: `manual-${sectionKey}-${Date.now()}`,
        text,
        origin: 'manual',
        type: 'source_note',
    };
}

function shouldShowDocument(document: AIManagerKnowledgeDocument) {
    return (
        document.isActive &&
        (document.documentType === 'company_profile' ||
            document.tags.includes('setup-v2') ||
            Boolean(document.metadata.approved_profile || document.metadata.approvedProfile))
    );
}

export default function KnowledgeBaseScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const [documents, setDocuments] = useState<AIManagerKnowledgeDocument[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [title, setTitle] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [profile, setProfile] = useState<AIManagerDisplayKnowledgeProfile>(() => createEmptyProfile());
    const [newFactSection, setNewFactSection] = useState<AIManagerKnowledgeSectionKey>('offer');
    const [newFactText, setNewFactText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const selectedDocument = useMemo(
        () => documents.find((document) => document.id === selectedId) || null,
        [documents, selectedId]
    );
    const visibleDocuments = useMemo(() => {
        const seen = new Set<string>();
        return documents.filter((document) => {
            const profile = getProfileFromDocument(document);
            const sourceKey = getSourceLabel(document.sourceUrl).toLowerCase();
            const previewKey = getProfilePreview(profile, document.summary || document.body).toLowerCase();
            const key = `${sourceKey}|${previewKey || document.title.toLowerCase()}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }, [documents]);
    const normalizedProfile = useMemo(() => withCompleteSections(profile), [profile]);
    const selectedDocumentProfile = useMemo(
        () => (selectedDocument ? getProfileFromDocument(selectedDocument) : createEmptyProfile()),
        [selectedDocument]
    );
    const dirty = useMemo(() => {
        if (!selectedDocument) {
            return false;
        }
        return (
            title.trim() !== selectedDocument.title ||
            sourceUrl.trim() !== selectedDocument.sourceUrl ||
            JSON.stringify(toBackendProfile(normalizedProfile)) !==
                JSON.stringify(toBackendProfile(selectedDocumentProfile))
        );
    }, [normalizedProfile, selectedDocument, selectedDocumentProfile, sourceUrl, title]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            try {
                const loaded = (await AIManagersService.listAllKnowledgeDocuments())
                    .filter(shouldShowDocument)
                    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
                if (cancelled) {
                    return;
                }
                setDocuments(loaded);
                const first = loaded[0] || null;
                if (first) {
                    hydrateDocument(first);
                }
            } catch (_error) {
                if (!cancelled) {
                    enqueueSnackbar('Не удалось загрузить базы знаний', { variant: 'error' });
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [enqueueSnackbar]);

    const hydrateDocument = (document: AIManagerKnowledgeDocument) => {
        setSelectedId(document.id);
        setTitle(document.title || 'Профиль организации');
        setSourceUrl(document.sourceUrl || '');
        setProfile(getProfileFromDocument(document));
        setNewFactText('');
    };

    const updateFact = (sectionKey: AIManagerKnowledgeSectionKey, itemId: string, text: string) => {
        setProfile((current) => ({
            ...current,
            sections: withCompleteSections(current).sections.map((section) =>
                section.key === sectionKey
                    ? {
                          ...section,
                          items: section.items.map((item) => (item.id === itemId ? { ...item, text } : item)),
                      }
                    : section
            ),
        }));
    };

    const deleteFact = (sectionKey: AIManagerKnowledgeSectionKey, itemId: string) => {
        setProfile((current) => ({
            ...current,
            sections: withCompleteSections(current).sections.map((section) =>
                section.key === sectionKey
                    ? { ...section, items: section.items.filter((item) => item.id !== itemId) }
                    : section
            ),
        }));
    };

    const addFact = () => {
        const text = newFactText.trim();
        if (!text) {
            enqueueSnackbar('Добавьте текст факта', { variant: 'info' });
            return;
        }
        setProfile((current) => ({
            ...current,
            sections: withCompleteSections(current).sections.map((section) =>
                section.key === newFactSection
                    ? { ...section, items: [...section.items, buildDisplayItem(newFactSection, text)] }
                    : section
            ),
        }));
        setNewFactText('');
    };

    const saveDocument = async () => {
        if (!selectedDocument) {
            return;
        }
        const completedProfile = withCompleteSections(profile);
        if (getProfileItemCount(completedProfile) === 0) {
            enqueueSnackbar('Добавьте хотя бы один факт', { variant: 'info' });
            return;
        }

        setSaving(true);
        try {
            const backendProfile = toBackendProfile(completedProfile);
            const body = buildProfileBody(completedProfile);
            const saved = await AIManagersService.updateKnowledgeDocument(selectedDocument.id, {
                title: title.trim() || 'Профиль организации',
                sourceUrl: sourceUrl.trim(),
                summary: buildProfileSummary(completedProfile).slice(0, 1800),
                body,
                documentType: selectedDocument.documentType || 'company_profile',
                tags: Array.from(new Set([...selectedDocument.tags, 'leadsmania2', 'knowledge-base'])),
                metadata: {
                    ...selectedDocument.metadata,
                    approved_profile: backendProfile,
                    knowledge_display_profile: backendProfile,
                    knowledge_base_updated_at: new Date().toISOString(),
                },
                isActive: true,
            });
            setDocuments((current) =>
                current
                    .map((document) => (document.id === saved.id ? saved : document))
                    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
            );
            hydrateDocument(saved);
            enqueueSnackbar('База знаний сохранена', { variant: 'success' });
        } catch (_error) {
            enqueueSnackbar('Не удалось сохранить базу знаний', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <DesktopLayout hideRightSidebar>
            <Stack
                spacing={2.5}
                minHeight="100%"
                sx={{ minWidth: 0, px: { xs: 2, md: 0 }, py: { xs: 2, md: 0 } }}
            >
                <Box
                    display="flex"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    gap={2}
                    flexDirection={{ xs: 'column', md: 'row' }}
                >
                    <Box>
                        <Typography variant="h1">База знаний</Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: 'var(--lm-muted)', mt: 1, maxWidth: 680 }}
                        >
                            Профили компаний и объектов для AI-менеджеров
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => history.push(ROUTES_URL.AI_MANAGER_NEW)}
                        sx={{ borderRadius: CARD_RADIUS, textTransform: 'none', fontWeight: 800 }}
                    >
                        Создать AI-менеджера
                    </Button>
                </Box>

                {loading ? (
                    <Box
                        minHeight={360}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <CircularProgress />
                    </Box>
                ) : visibleDocuments.length === 0 ? (
                    <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 900 }}>Баз пока нет</Typography>
                        <Typography sx={{ color: 'var(--lm-muted)', mt: 0.75 }}>
                            Первая база появится после анализа сайта при создании AI-менеджера
                        </Typography>
                    </Paper>
                ) : (
                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', lg: '340px minmax(0, 1fr)' }}
                        gap={2}
                        alignItems="start"
                        sx={{ minWidth: 0 }}
                    >
                        <Stack spacing={1.25}>
                            {visibleDocuments.map((document) => {
                                const documentProfile = getProfileFromDocument(document);
                                const selected = selectedId === document.id;
                                const preview = getProfilePreview(documentProfile, document.summary || document.body);
                                return (
                                    <Box
                                        key={document.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => hydrateDocument(document)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                hydrateDocument(document);
                                            }
                                        }}
                                        sx={{
                                            ...cardSx,
                                            cursor: 'pointer',
                                            p: 1.5,
                                            borderColor: selected ? 'var(--lm-accent-ink)' : BORDER,
                                            bgcolor: selected ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="flex-start"
                                            justifyContent="space-between"
                                            gap={1}
                                        >
                                            <Box minWidth={0}>
                                                <Typography sx={{ fontWeight: 900, lineHeight: 1.25 }}>
                                                    {document.title || 'Профиль организации'}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'var(--lm-muted)',
                                                        mt: 0.5,
                                                        fontSize: 14,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getSourceLabel(document.sourceUrl)}
                                                </Typography>
                                            </Box>
                                            {selected && (
                                                <IconCheck
                                                    size={18}
                                                    color="var(--lm-accent-ink)"
                                                />
                                            )}
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            mt={1}
                                            flexWrap="wrap"
                                        >
                                            <Chip
                                                size="small"
                                                label={formatFacts(getProfileItemCount(documentProfile))}
                                                sx={{
                                                    bgcolor: 'var(--lm-accent-soft)',
                                                    color: 'var(--lm-accent-ink)',
                                                    fontWeight: 800,
                                                }}
                                            />
                                            {formatDate(document.updatedAt) && (
                                                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>
                                                    {formatDate(document.updatedAt)}
                                                </Typography>
                                            )}
                                        </Box>
                                        {preview && (
                                            <Typography
                                                sx={{
                                                    color: 'var(--lm-muted)',
                                                    mt: 1,
                                                    fontSize: 14,
                                                    lineHeight: 1.4,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {preview}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>

                        {selectedDocument && (
                            <Paper sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
                                <Stack spacing={2.25}>
                                    <Box
                                        display="flex"
                                        alignItems={{ xs: 'flex-start', md: 'center' }}
                                        justifyContent="space-between"
                                        gap={1.5}
                                        flexDirection={{ xs: 'column', md: 'row' }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontSize: 22, fontWeight: 900 }}>
                                                Профиль организации
                                            </Typography>
                                            <Typography sx={{ color: 'var(--lm-muted)', mt: 0.35 }}>
                                                Эти факты можно использовать при создании AI-менеджера
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            startIcon={
                                                saving ? (
                                                    <CircularProgress
                                                        size={16}
                                                        color="inherit"
                                                    />
                                                ) : (
                                                    <IconDeviceFloppy size={18} />
                                                )
                                            }
                                            disabled={!dirty || saving}
                                            onClick={saveDocument}
                                            sx={{ borderRadius: CARD_RADIUS, textTransform: 'none', fontWeight: 900 }}
                                        >
                                            Сохранить
                                        </Button>
                                    </Box>

                                    <Box
                                        display="grid"
                                        gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }}
                                        gap={1.5}
                                    >
                                        <TextField
                                            label="Название"
                                            value={title}
                                            onChange={(event) => setTitle(event.target.value)}
                                            sx={textFieldSx}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Источник"
                                            value={sourceUrl}
                                            onChange={(event) => setSourceUrl(event.target.value)}
                                            sx={textFieldSx}
                                            fullWidth
                                            placeholder="https://site.ru"
                                        />
                                    </Box>

                                    <Box sx={{ ...cardSx, p: 1.5, bgcolor: 'var(--lm-canvas)' }}>
                                        <Box
                                            display="grid"
                                            gridTemplateColumns="1fr"
                                            gap={1}
                                            alignItems="start"
                                        >
                                            <TextField
                                                select
                                                label="Тип"
                                                value={newFactSection}
                                                onChange={(event) =>
                                                    setNewFactSection(
                                                        event.target.value as AIManagerKnowledgeSectionKey
                                                    )
                                                }
                                                sx={{ ...textFieldSx, maxWidth: { md: 260 } }}
                                                fullWidth
                                            >
                                                {KNOWLEDGE_SECTIONS.map((section) => (
                                                    <MenuItem
                                                        key={section.key}
                                                        value={section.key}
                                                    >
                                                        {section.title}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                label="Новый факт"
                                                value={newFactText}
                                                onChange={(event) => setNewFactText(event.target.value)}
                                                sx={textFieldSx}
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                placeholder="Например: минимальный чек от 30 000 рублей"
                                            />
                                            <Button
                                                variant="outlined"
                                                startIcon={<IconPlus size={18} />}
                                                onClick={addFact}
                                                sx={{
                                                    borderRadius: CARD_RADIUS,
                                                    textTransform: 'none',
                                                    fontWeight: 900,
                                                    minHeight: 44,
                                                    px: 2,
                                                    justifySelf: { xs: 'stretch', md: 'flex-start' },
                                                }}
                                            >
                                                Добавить
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Stack spacing={1.25}>
                                        {normalizedProfile.sections
                                            .filter((section) => section.items.some((item) => item.text.trim()))
                                            .map((section) => (
                                                <KnowledgeSection
                                                    key={section.key}
                                                    section={section}
                                                    onChange={updateFact}
                                                    onDelete={deleteFact}
                                                />
                                            ))}
                                        {normalizedProfile.sections.some(
                                            (section) => !section.items.some((item) => item.text.trim())
                                        ) && (
                                            <Box
                                                sx={{
                                                    border: `1px dashed ${BORDER}`,
                                                    borderRadius: CARD_RADIUS,
                                                    color: 'var(--lm-muted)',
                                                    px: 1.5,
                                                    py: 1.25,
                                                    fontSize: 14,
                                                }}
                                            >
                                                Без фактов:{' '}
                                                {normalizedProfile.sections
                                                    .filter(
                                                        (section) => !section.items.some((item) => item.text.trim())
                                                    )
                                                    .map((section) => section.title)
                                                    .join(', ')}
                                            </Box>
                                        )}
                                    </Stack>
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                )}
            </Stack>
        </DesktopLayout>
    );
}

function KnowledgeSection({
    section,
    onChange,
    onDelete,
}: {
    section: AIManagerDisplayKnowledgeSection;
    onChange: (sectionKey: AIManagerKnowledgeSectionKey, itemId: string, text: string) => void;
    onDelete: (sectionKey: AIManagerKnowledgeSectionKey, itemId: string) => void;
}) {
    const items = section.items.filter((item) => item.text.trim());

    return (
        <Box sx={{ ...cardSx, p: 1.5 }}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                mb={items.length ? 1.25 : 0}
            >
                <Typography sx={{ fontWeight: 900 }}>{section.title}</Typography>
                <Chip
                    size="small"
                    label={items.length}
                    sx={{ bgcolor: 'var(--lm-canvas)', fontWeight: 800 }}
                />
            </Box>

            {items.length ? (
                <Stack spacing={1}>
                    {items.map((item) => (
                        <Box
                            key={item.id}
                            display="grid"
                            gridTemplateColumns={{ xs: '1fr', md: 'minmax(0, 1fr) 40px' }}
                            gap={1}
                            alignItems="start"
                        >
                            <TextField
                                value={item.text}
                                onChange={(event) => onChange(section.key, item.id, event.target.value)}
                                sx={textFieldSx}
                                fullWidth
                                multiline
                                minRows={1}
                            />
                            <Tooltip title="Удалить факт">
                                <IconButton
                                    onClick={() => onDelete(section.key, item.id)}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: CARD_RADIUS,
                                        color: 'var(--lm-danger-ink)',
                                        border: `1px solid ${BORDER}`,
                                    }}
                                >
                                    <IconTrash size={18} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14 }}>Фактов пока нет</Typography>
            )}
        </Box>
    );
}
